import { Address, BaseError } from "@silo/types";
import { Effect, pipe, Option } from "effect";
import { RawUserReward, UserMerklRewardMeta } from "./types";
import { KvClient } from "../kv-client";
import { Schema } from "@effect/schema";
import { getPricingToken } from "./get-pricing-token";
import { AGLA_MERKL_ADDRESS } from "./constants";

export class InvalidRewardError extends BaseError {
  public readonly _tag = "InvalidRewardError";
}

export type GetUserRewardMeta = ReturnType<typeof getUserRewardMeta>;
export type GetUserRewardMetaSuccess = Effect.Effect.Success<GetUserRewardMeta>;
export type GetUserRewardMetaError = Effect.Effect.Error<GetUserRewardMeta>;
export type GetUserRewardMetaContext = Effect.Effect.Context<GetUserRewardMeta>;

export function getUserRewardMeta(rawUserReward: RawUserReward) {
  return Effect.gen(function* () {
    const {
      chainKey,
      reasonKey,
      rewardTokenDecimals,
      rewardTokenSymbol,
      unclaimed,
    } = rawUserReward;

    const shareTokenAddress = yield* pipe(
      reasonKey.split("Forwarder_ERC20_").pop(),
      Option.fromNullable,
      Effect.andThen(Schema.decode(Address))
    );

    const { protocolKey, marketAddress, tokenAddress, positionType } =
      yield* pipe(
        KvClient.getShareTokenMeta(chainKey, shareTokenAddress),
        Effect.andThen((option) => option)
      );

    const rewardTokenAddress = yield* pipe(
      rawUserReward.rewardTokenAddress,
      Schema.decode(Address)
    );

    yield* rewardTokenAddress === AGLA_MERKL_ADDRESS
      ? Effect.fail(new InvalidRewardError({ message: "aglaMerkl token" }))
      : Effect.void;

    const { pricingTokenAddress, pricingTokenSymbol } = getPricingToken({
      chainKey,
      rewardTokenAddress,
      rewardTokenSymbol,
    });

    const userRewardMeta: UserMerklRewardMeta = {
      protocolKey,
      chainKey,
      marketAddress,
      tokenAddress,
      rewardTokenAddress,
      rewardTokenDecimals,
      rewardTokenSymbol,
      pricingTokenAddress,
      pricingTokenSymbol,
      shareTokenAddress,
      positionType,
      unclaimed,
    };

    return userRewardMeta;
  });
}
